import { useEffect, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { AddressInput } from "@/components/AddressInput";
import { MapView } from "@/components/MapView";
import { FoodRequest, emptyFood, foodPayload, foodBlocked } from "@/components/FoodRequest";
import { CardOnFile as PaymentGate } from "@/components/CardOnFile";
import { RequestDriver } from "@/components/RequestDriver";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOCATION_REQUIRED_MSG, useCustomerLocation } from "@/context/CustomerLocationContext";
import { LocationRequiredBanner } from "@/components/LocationRequired";
import { PaymentMethodPicker, afterBooking, payLabel } from "@/components/PaymentMethodPicker";

export const RentalBreakdown = ({ fare, overtime }) => (
  <div data-testid="rental-breakdown" className="space-y-1.5 text-sm">
    <div className="flex justify-between text-zinc-300"><span>Hours ({fare.hours} × {money(fare.rate)}/hr)</span><span data-testid="rental-subtotal">{money(fare.subtotal)}</span></div>
    <div className="flex justify-between text-zinc-300"><span className="flex items-center gap-1.5">Tip ({fare.tip_percent}%) <Lock className="h-3 w-3 text-amber-400" /><span className="text-[10px] text-zinc-500">required</span></span><span data-testid="rental-tip">{money(fare.tip)}</span></div>
    <div className="flex justify-between text-zinc-300"><span>Tax ({fare.tax_rate}% on hourly subtotal)</span><span data-testid="rental-tax">{money(fare.tax)}</span></div>
    <div className="flex justify-between border-t border-amber-500/20 pt-2"><span className="font-serif text-lg text-amber-100">Total</span><span data-testid="rental-total" className="gold-text font-serif text-2xl font-semibold">{money(fare.total)}</span></div>
    {overtime?.amount > 0 && <div data-testid="rental-overtime" className={`flex justify-between ${overtime.status === "payment_due" ? "text-red-300" : "text-zinc-300"}`}><span>Overtime ({overtime.hours}h + tip + tax) · {overtime.status === "payment_due" ? "Payment due" : "charged"}</span><span>{money(overtime.amount)}</span></div>}
  </div>
);

export const RentalPanel = () => {
  const { user } = useAuth();
  const [pickup, setPickup] = useState({ text: "", point: null, error: "" });
  const [hours, setHours] = useState("4");
  const [startAt, setStartAt] = useState("");
  const [vc, setVc] = useState("premium");
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [food, setFood] = useState(emptyFood());
  const [requested, setRequested] = useState(null);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("card_on_file");

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/rentals/quote", { hours: Number(hours) || 0, vehicle_class: vc })
        .then(({ data }) => { setQuote(data); setError(""); })
        .catch((e) => { setQuote(null); setError(errMsg(e)); });
    }, 300);
    return () => clearTimeout(t);
  }, [hours, vc]);

  const { status: gps, sendNow } = useCustomerLocation();
  const book = async () => {
    setBusy(true);
    if (!(await sendNow())) { toast.error(LOCATION_REQUIRED_MSG); setBusy(false); return; }
    try {
      const { data } = await api.post("/rentals", { hours: Number(hours), vehicle_class: vc, pickup: pickup.point, start_at: startAt ? new Date(startAt).toISOString() : null, origin_url: window.location.origin, ...foodPayload(food), requested_driver_code: requested?.driver_code || null, payment_method: method });
      afterBooking(data);
      if (!data.checkout_url) setBusy(false);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,420px)_1fr] lg:gap-8" data-testid="rental-panel">
      <div className="order-2 min-w-0 space-y-6 lg:order-1">
        <LocationRequiredBanner />
        <PaymentGate />
        <div className="lux-card space-y-5 p-5 sm:p-6">
          <AddressInput label="Pickup" testId="rental-pickup-input" placeholder="Pickup address" value={pickup} onChange={setPickup} locate />
          <label className="block"><span className="lux-eyebrow mb-2 block">Start (leave empty for now)</span>
            <input type="datetime-local" data-testid="rental-start-input" className="lux-input" value={startAt} onChange={(e) => setStartAt(e.target.value)} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="lux-eyebrow mb-2 block">Hours</span>
              <input type="number" min="1" max="72" data-testid="rental-hours-input" className="lux-input" value={hours} onChange={(e) => setHours(e.target.value)} /></label>
            <label className="block"><span className="lux-eyebrow mb-2 block">Class</span>
              <select data-testid="rental-class-select" className="lux-input" value={vc} onChange={(e) => setVc(e.target.value)}>
                <option value="premium">Premium</option><option value="standard">Standard</option><option value="shared">Shared</option></select></label>
          </div>
          {error && <p data-testid="rental-error" className="text-xs font-medium text-amber-500">{error}</p>}
          <p className="text-xs text-zinc-500">4–5h $175/hr · 6–8h $150/hr · 9h+ $110/hr — rate applies to all hours. No mileage, per-minute or wait charges.</p>
        </div>
        <RequestDriver value={requested} onChange={setRequested} />
        {requested && <p className="-mt-3 text-xs text-zinc-500">Your rental is shown only to {requested.first_name} for 30 minutes, then to all drivers.</p>}
        <FoodRequest value={food} onChange={setFood} />
        {quote && <div className="lux-card p-5 sm:p-6"><RentalBreakdown fare={quote} /></div>}
        <PaymentMethodPicker value={method} onChange={setMethod} testId="rental-payment-method-picker" />
        <button data-testid="rental-confirm-button" onClick={book} disabled={!user.payment_verified || gps !== "on" || !quote || !pickup.point || busy || foodBlocked(food, user)} className="btn-gold w-full !py-4 text-base">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {quote ? `Confirm rental & ${payLabel(method, quote.total)}` : "Confirm rental"}
        </button>
      </div>
      <div className="order-1 min-w-0 lg:order-2"><MapView className="h-[300px] sm:h-[420px]" pickup={pickup.point} testId="rental-map" /></div>
    </div>
  );
};
